import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { staffApi } from "@/api";

/**
 * App-level count of unconverted accepted quotes — quotes sitting at status
 * "accepted" awaiting provisioning (provisioning moves them to "converted", so
 * "accepted" IS the unconverted set). Feeds the nav badges on the Quotes
 * section and its Accepted sub-link. Mirrors the letters UnreadContext: polls
 * every 60s while the tab is visible (paused when hidden, immediate refresh on
 * return), and the provision workbench invalidates it on commit.
 *
 * Reuses `staffApi.listQuotes` like the AcceptedQueue (no count endpoint) —
 * the envelope's `itemsTotal` is preferred so the badge stays right past one
 * page, with the item count as the bare-array fallback.
 */

const POLL_MS = 60_000;

interface AcceptedCountState {
  /** Unconverted accepted quotes; null until the first load succeeds. */
  count: number | null;
  refresh: () => Promise<void>;
}

const AcceptedCountCtx = createContext<AcceptedCountState>({
  count: null,
  refresh: async () => {},
});

export function AcceptedCountProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await staffApi.listQuotes({ status: "accepted", per_page: 100 });
      setCount(
        Array.isArray(res)
          ? res.length
          : typeof res.itemsTotal === "number"
            ? res.itemsTotal
            : res.items?.length ?? 0,
      );
    } catch {
      /* keep the last good value rather than flicker to zero */
    }
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (!interval) interval = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void refresh(); // instant on return
        start();
      }
    };

    void refresh();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  const value = useMemo<AcceptedCountState>(() => ({ count, refresh }), [count, refresh]);
  return <AcceptedCountCtx.Provider value={value}>{children}</AcceptedCountCtx.Provider>;
}

export function useAcceptedCount(): AcceptedCountState {
  return useContext(AcceptedCountCtx);
}
